import camelCase from 'lodash/camelCase';
import { call, fork, put, select, spawn, take, takeEvery } from 'typed-redux-saga/macro';

import { debugApp as debug } from 'Lib/debug';
import { processResponse } from 'Lib/reduxUtils';
import { withErrorHandling } from 'Lib/sagaHelpers';
import createKeyProcessor from 'Lib/utils/createKeyProcessor';
import getDeviceId from 'Lib/utils/deviceId';
import services, { getServiceFn } from 'Services';
import { EXPERIMENTS_SERVICE } from 'Services/constants';

import { initialAuthCheckComplete } from 'Store/lifecycles';
import { bucketsResponseSchema } from 'Store/meta/schemas';
import { reauth, setAuthFunnelSessionId } from 'Store/mfa/actions';
import { LOGIN_SUCCESS } from 'Store/mfa/constants';
import { getAuthFunnelSessionId, getAuthTokenExpiration } from 'Store/mfa/selectors';
import {
  DECISION_INTERACTIVE_LOGIN,
  DECISION_LOGGED_IN,
  DECISION_REAUTH,
  DECISION_YES,
  STEP_AUTH_END,
  STEP_TOKEN_TYPE,
  type Decision,
} from 'Store/mfa/tracking';
import { postLoginSaga } from 'Store/user/logic';
import { getIsUserLoggedIn, getIsUserLoggedInAsGuest } from 'Store/user/selectors';

import { trackEvent } from 'Store/sparks/actions';
import { IDENTITY_INTERACT } from 'Store/sparks/sparks.events';

import { getExperiments, getExperimentsError, getExperimentsSuccess } from '../actions';
import { GET_EXPERIMENTS } from '../constants';

const processBucketsResponseKeys = createKeyProcessor((key: string, pathParts?: string[]) => {
  // Do not process lever names (data.levers.*)
  if (pathParts?.length === 2 && pathParts[1] === 'levers') {
    return key;
  }
  return camelCase(key);
});

export function* handleLoginFlow() {
  yield* takeEvery(GET_EXPERIMENTS, getExperimentsSaga);
  yield* put(setAuthFunnelSessionId());

  const isLoggedIn = yield* select(getIsUserLoggedIn);
  const isLoggedInAsGuest = yield* select(getIsUserLoggedInAsGuest);

  if (isLoggedIn) {
    // Prevent 401s by reauthing if we know the API token is expired
    const authTokenExpiration = yield* select(getAuthTokenExpiration);
    const isExpired = authTokenExpiration && authTokenExpiration < Date.now();
    if (isExpired) {
      yield* fork(trackAuthDecision, DECISION_REAUTH);
      yield* put(reauth());
      yield* take(LOGIN_SUCCESS);
    } else {
      yield* fork(trackAuthDecision, DECISION_LOGGED_IN);
    }
    yield* spawn(postLoginSaga);
  } else if (!isLoggedInAsGuest) {
    yield* fork(trackAuthDecision, DECISION_INTERACTIVE_LOGIN);
    // Clean up old/outdated auth tokens to fix: https://gotinder.atlassian.net/browse/TDP-36589
    if (services.apiClient) {
      yield* call([services.apiClient, services.apiClient.logout]);
    }

    // Load pre-auth levers
    yield* put(getExperiments());
  }
  yield* call(initialAuthCheckComplete.trigger);
}

function* trackAuthDecision(decision: Decision) {
  const funnelSessionId = yield* select(getAuthFunnelSessionId);
  yield* put(
    trackEvent(IDENTITY_INTERACT, {
      stepName: STEP_TOKEN_TYPE,
      decision,
      origin: 'client',
      funnelSessionId,
    })
  );
  if (decision === DECISION_LOGGED_IN) {
    yield* put(
      trackEvent(IDENTITY_INTERACT, {
        stepName: STEP_AUTH_END,
        decision: DECISION_YES,
        origin: 'client',
        funnelSessionId,
      })
    );
  }
}

export const getExperimentsSaga = withErrorHandling(
  function* _getExperimentsSaga() {
    const deviceUuid = getDeviceId();
    const getExperimentsFunc = yield* call(
      getServiceFn,
      EXPERIMENTS_SERVICE,
      'getExperiments' as const
    );
    const response = yield* call(getExperimentsFunc, deviceUuid);

    const payload = yield processResponse(
      response,
      bucketsResponseSchema,
      processBucketsResponseKeys
    );

    yield* put(getExperimentsSuccess(payload));
  },
  {
    name: 'getExperimentsSaga',
    *errorHandler(err) {
      debug('Error loading experiments', err);
      yield* put(getExperimentsError(err));
    },
  }
);
